/**
 * Anam Avatar Provider
 *
 * Implementation of IAvatarProvider for Anam AI streaming avatars.
 * Uses the @anam-ai/js-sdk with server-side session tokens.
 *
 * Key differences from Tavus:
 * - Session tokens obtained from our backend proxy (API key stays server-side)
 * - SDK manages WebRTC internally (no Daily.co dependency)
 * - Echo uses TalkMessageStream instead of Daily app messages
 * - Video element must exist in DOM before streaming (SDK attaches to element by ID)
 */

import { createClient, AnamEvent, ConnectionClosedCode } from '@anam-ai/js-sdk'
import type { AnamClient, MessageStreamEvent } from '@anam-ai/js-sdk'

import { apiClient } from '@/services/apiClient'
import {
  BUILT_IN_CLIENT_TOOLS,
  registerBuiltInToolHandlers,
} from './anamClientTools'

import type {
  IAvatarProvider,
  AvatarInfo,
  AvatarSessionOptions,
  HealthStatus,
  AvatarSession,
  AvatarSessionState,
  AvatarSessionEvents,
  ExpressionType,
} from './IAvatarProvider'

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export class AnamProvider implements IAvatarProvider {
  readonly id = 'anam'
  readonly name = 'Anam'
  readonly type = 'anam' as const

  private _initialized = false
  private cachedAvatars: AvatarInfo[] = []

  async initialize(_apiKey: string): Promise<void> {
    // Anam uses session tokens from the backend, not a direct API key.
    // Initialization verifies that we can reach our backend proxy.
    const health = await this.healthCheck()
    if (!health.isHealthy) {
      throw new Error(health.message || 'Failed to connect to Anam backend proxy')
    }
    this._initialized = true
  }

  isInitialized(): boolean {
    return this._initialized
  }

  async getAvatars(): Promise<AvatarInfo[]> {
    const response = await apiClient.fetch('/api/avatar/anam-avatars')
    if (!response.ok) {
      throw new Error(`Failed to fetch Anam avatars: ${response.statusText}`)
    }

    // Anam API may return a bare array or a wrapper object (e.g. { avatars: [...] })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw: any = await response.json()
    const items: any[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw.avatars)
        ? raw.avatars
        : Array.isArray(raw.data)
          ? raw.data
          : Array.isArray(raw.results)
            ? raw.results
            : []

    this.cachedAvatars = items.map((a) => ({
      id: a.id || a.avatarId,
      name: a.name || a.avatarName || 'Unnamed Avatar',
      description: a.description,
      previewUrl: a.preview_image_url || a.previewImageUrl,
      gender: a.gender as AvatarInfo['gender'],
    }))

    return this.cachedAvatars
  }

  async createSession(
    options: AvatarSessionOptions,
    events: AvatarSessionEvents
  ): Promise<AvatarSession> {
    return new AnamSession(options, events)
  }

  async healthCheck(): Promise<HealthStatus> {
    try {
      const start = Date.now()
      const response = await apiClient.fetch('/api/avatar/anam-avatars')
      const latency = Date.now() - start

      return {
        isHealthy: response.ok,
        latency,
        message: response.ok ? 'Connected' : response.statusText,
      }
    } catch (error) {
      return {
        isHealthy: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async destroy(): Promise<void> {
    this._initialized = false
    this.cachedAvatars = []
  }
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/** Debounce delay for end-of-turn detection (ms). */
const ECHO_END_DELAY_MS = 2000

/**
 * Anam Avatar Session
 *
 * Manages an Anam streaming avatar session using the @anam-ai/js-sdk.
 * Supports two pipeline modes:
 *   - anam_ai:    Anam handles mic → STT → LLM → TTS → avatar
 *   - expert_rag: We handle mic (Deepgram STT) → RAG → echo text back to Anam TTS → avatar
 */
class AnamSession implements AvatarSession {
  private _state: AvatarSessionState = 'idle'
  private client: AnamClient | null = null
  private _videoElement: HTMLVideoElement | null = null
  private videoElementId: string | null = null

  // TalkMessageStream for progressive echo (expert_rag mode)
  private talkStream: ReturnType<AnamClient['createTalkMessageStream']> | null =
    null
  private echoEndTimer: ReturnType<typeof setTimeout> | null = null

  // Client tools cleanup function
  private cleanupToolHandlers: (() => void) | null = null

  constructor(
    private options: AvatarSessionOptions,
    private events: AvatarSessionEvents
  ) {}

  get state(): AvatarSessionState {
    return this._state
  }

  get videoElement(): HTMLVideoElement | null {
    return this._videoElement
  }

  private setState(newState: AvatarSessionState) {
    this._state = newState
    this.events.onStateChange?.(newState)
  }

  private get isExpertRagMode(): boolean {
    return this.options.pipelineMode === 'expert_rag'
  }

  async start(): Promise<void> {
    if (this.client) {
      throw new Error('Session already started')
    }

    this.setState('connecting')

    try {
      // 1. Request session token from our backend
      const sessionToken = await this.requestSessionToken()

      // 2. Create Anam client with appropriate options
      this.client = createClient(sessionToken, {
        disableInputAudio: this.isExpertRagMode,
      })

      // 3. Set up event listeners
      this.setupEventListeners()

      // 4. Register client tool handlers if enabled
      if (this.options.anamEnableClientTools && this.client) {
        this.cleanupToolHandlers = registerBuiltInToolHandlers(
          this.client,
          this.events
        )
      }

      // 5. Create a temporary video element for streaming.
      //    Anam's streamToVideoElement() requires an element in the DOM.
      //    The UI will reparent this element via onVideoReady.
      this.videoElementId = `anam-video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const video = document.createElement('video')
      video.id = this.videoElementId
      video.autoplay = true
      video.playsInline = true
      video.style.position = 'absolute'
      video.style.width = '0'
      video.style.height = '0'
      video.style.opacity = '0'
      video.style.pointerEvents = 'none'
      document.body.appendChild(video)
      this._videoElement = video

      // 6. Start streaming to the video element
      await this.client.streamToVideoElement(this.videoElementId)

      this.setState('connected')
    } catch (error) {
      console.error('Failed to start Anam session:', error)
      this.setState('error')
      this.events.onError?.(
        error instanceof Error ? error : new Error('Failed to start session')
      )
      throw error
    }
  }

  async stop(): Promise<void> {
    // Clear echo state
    this.clearEchoState()

    // Clean up client tool handlers
    if (this.cleanupToolHandlers) {
      this.cleanupToolHandlers()
      this.cleanupToolHandlers = null
    }

    // Stop streaming
    if (this.client) {
      try {
        await this.client.stopStreaming()
      } catch {
        // Already stopped — safe to ignore
      }
      this.client = null
    }

    // Remove the video element from the DOM
    if (this._videoElement) {
      this._videoElement.srcObject = null
      this._videoElement.remove()
      this._videoElement = null
    }
    this.videoElementId = null

    this.setState('idle')
  }

  sendAudio(_audioData: Float32Array | Int16Array): void {
    // In expert RAG mode, audio is captured by our AudioCaptureService
    // and sent to Deepgram STT — not to Anam.
    // In anam_ai mode, Anam handles mic input natively.
  }

  sendText(text: string): void {
    if (!this.client) {
      console.warn('Cannot send text: Anam client not connected')
      return
    }

    // In anam_ai mode, send text to Anam's built-in LLM
    this.client.talk(text)
    this.setState('thinking')
  }

  echo(text: string): void {
    if (!this.client) {
      console.warn('Cannot echo: Anam client not connected')
      return
    }

    // Clear any pending end-of-turn timer
    if (this.echoEndTimer) {
      clearTimeout(this.echoEndTimer)
      this.echoEndTimer = null
    }

    // Create a TalkMessageStream if we don't have one
    if (!this.talkStream || !this.talkStream.isActive()) {
      this.talkStream = this.client.createTalkMessageStream()
    }

    // Stream the sentence chunk (not end-of-speech yet)
    this.talkStream.streamMessageChunk(text, false)
    this.setState('speaking')

    // Set a debounce timer to finalize the turn if no more echo calls arrive
    this.echoEndTimer = setTimeout(() => {
      this.finalizeEcho()
    }, ECHO_END_DELAY_MS)
  }

  setExpression(_expression: ExpressionType): void {
    // Anam does not support explicit expression/emotion control
  }

  interrupt(): void {
    if (!this.client) return

    // Clear echo state
    this.clearEchoState()

    // Tell Anam to stop speaking
    this.client.interruptPersona()
    this.setState('ready')
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async requestSessionToken(): Promise<string> {
    const opts = this.options

    // Build persona config for the backend
    const personaConfig: Record<string, unknown> = {}

    if (opts.anamPersonaId) {
      // Reference a pre-configured Anam persona by ID
      personaConfig.persona_id = opts.anamPersonaId
    } else {
      // Build inline persona config
      if (opts.anamAvatarId) personaConfig.avatar_id = opts.anamAvatarId
      if (opts.anamVoiceId) personaConfig.voice_id = opts.anamVoiceId

      if (this.isExpertRagMode) {
        // Disable Anam's built-in AI — we provide our own via RAG
        personaConfig.llm_id = 'CUSTOMER_CLIENT_V1'
      } else if (opts.anamLlmId) {
        personaConfig.llm_id = opts.anamLlmId
      }

      if (opts.anamSystemPrompt) {
        personaConfig.system_prompt = opts.anamSystemPrompt
      }

      // Skip greeting if no initial message is set
      if (!opts.initialMessage) {
        personaConfig.skip_greeting = true
      }

      if (opts.anamLanguageCode) {
        personaConfig.language_code = opts.anamLanguageCode
      }
    }

    const body: Record<string, unknown> = {
      persona_config: personaConfig,
      disable_input_audio: this.isExpertRagMode,
      enable_session_replay: opts.anamEnableRecording ?? true,
    }

    // Include client tools if enabled
    if (opts.anamEnableClientTools) {
      body.client_tools = BUILT_IN_CLIENT_TOOLS
    }

    const response = await apiClient.fetch('/api/avatar/anam-session', {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        (errorData as { detail?: string }).detail ||
          `Failed to get Anam session token (${response.status})`
      )
    }

    const data = await response.json()
    return (data as { session_token: string }).session_token
  }

  private setupEventListeners(): void {
    if (!this.client) return

    // Session ready
    this.client.addListener(AnamEvent.SESSION_READY, () => {
      this.setState('ready')
    })

    // Video started playing — notify UI so it can display the element
    this.client.addListener(AnamEvent.VIDEO_PLAY_STARTED, () => {
      if (this._videoElement) {
        this.events.onVideoReady?.(this._videoElement)
      }
      this.setState('ready')
    })

    // Real-time message streaming (transcription / persona speech)
    this.client.addListener(
      AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED,
      (event: MessageStreamEvent) => {
        if (event.role === 'persona') {
          this.events.onMessage?.(event.content)
          if (event.endOfSpeech) {
            this.setState('ready')
          }
        } else if (event.role === 'user') {
          this.events.onTranscript?.(event.content, event.endOfSpeech)
        }
      }
    )

    // Talk stream interrupted by user speech
    this.client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, () => {
      this.clearEchoState()
      this.setState('ready')
    })

    // Connection closed
    this.client.addListener(
      AnamEvent.CONNECTION_CLOSED,
      (reason: ConnectionClosedCode, details?: string) => {
        if (reason !== ConnectionClosedCode.NORMAL) {
          this.events.onError?.(
            new Error(
              `Anam connection closed: ${reason}${details ? ` - ${details}` : ''}`
            )
          )
          this.setState('error')
        } else {
          this.setState('disconnected')
        }
      }
    )

    // Microphone permission denied
    this.client.addListener(
      AnamEvent.MIC_PERMISSION_DENIED,
      (error: string) => {
        this.events.onError?.(
          new Error(`Microphone permission denied: ${error}`)
        )
      }
    )

    // Server warnings
    this.client.addListener(AnamEvent.SERVER_WARNING, (message: string) => {
      console.warn('[AnamSession] Server warning:', message)
    })
  }

  /** Finalize the current TalkMessageStream (end-of-turn). */
  private async finalizeEcho(): Promise<void> {
    if (this.talkStream && this.talkStream.isActive()) {
      try {
        await this.talkStream.endMessage()
      } catch {
        // Stream may already be ended — safe to ignore
      }
    }
    this.talkStream = null
    this.echoEndTimer = null
  }

  /** Clear all echo-related state. */
  private clearEchoState(): void {
    if (this.echoEndTimer) {
      clearTimeout(this.echoEndTimer)
      this.echoEndTimer = null
    }
    if (this.talkStream && this.talkStream.isActive()) {
      try {
        this.talkStream.endMessage()
      } catch {
        // Safe to ignore
      }
    }
    this.talkStream = null
  }
}

// Export singleton instance
export const anamProvider = new AnamProvider()
